import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, message } from "antd";
import { Plus, Trash } from "@phosphor-icons/react";
import {
  BtnRow,
  CostLineMeta,
  CostLineRow,
  CostLinesTable,
  CostTotalRow,
  DegradedBanner,
  EmailPreview,
  FieldGroup,
  FieldLabel,
  LinkBtn,
  LoadingState,
  MetaText,
  MutedText,
  PaymentPill,
  SecondaryBtn,
  SendBtn,
  UnsentBanner,
  SectionHeading,
  SentBanner,
  TextInput,
  WarningBanner,
  WarningBody,
  WarningHeader,
  WarningLabel,
} from "../FeeNoticeQueueStyled";
import { describePaymentStatus } from "../utils";
import { downloadFeeNoticeInvoicePdfAPI } from "../../../service/feeNotices";
import {
  fetchFeeNoticeInvoice,
  saveFeeNoticeInvoice,
  sendFeeNoticeInvoice,
} from "../../../slice/FeeNoticeSlice";

/**
 * View / edit / send the client cost-reimbursement invoice without
 * leaving the letter (Matthew, 2026-07-27). The backend assembles the
 * invoice from the letter's extracted data plus the matter's live
 * Salesforce cost ledger; everything here is editable because the SF
 * cost labels are hand-entered ("CLaim File Process") and the finance
 * team rewords them for the client.
 *
 * The email subject/body preview is SERVER-composed — after edits it
 * refreshes on Save, so what the preview shows is exactly what Send
 * will mail. Send requires a save first for the same reason.
 */

const money = (value) => {
  const parsed = Number.parseFloat(String(value ?? "").replace(/[$,]/g, ""));
  if (!Number.isFinite(parsed)) return "";
  return `$${parsed.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const lineTotal = (lines) =>
  lines.reduce((sum, line) => {
    const parsed = Number.parseFloat(
      String(line.amount ?? "").replace(/[$,]/g, "")
    );
    return sum + (Number.isFinite(parsed) ? parsed : 0);
  }, 0);

const InvoiceSection = ({ disabled = false }) => {
  const dispatch = useDispatch();
  const {
    currentItem,
    invoice: payload,
    invoiceLoading,
    invoiceSaving,
    invoiceSending,
    invoiceError,
  } = useSelector((state) => state.feeNotices);

  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [decisionSentence, setDecisionSentence] = useState("");
  const [award, setAward] = useState("");
  const [feePercent, setFeePercent] = useState("");
  const [attorneyFee, setAttorneyFee] = useState("");
  const [costLines, setCostLines] = useState([]);
  const [recipient, setRecipient] = useState("");
  const [dirty, setDirty] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // Reviewer chose to prepare an invoice the backend says isn't
  // warranted (extraction does miss awards).
  const [overrideWarrant, setOverrideWarrant] = useState(false);

  const invoice = payload?.invoice;

  // Fetch once per opened letter, tracked by id rather than by state
  // shape: keying the guard on `!payload && !invoiceLoading` re-fired
  // the moment a request FAILED, and since the pending reducer clears
  // invoiceError the panel sat on "Assembling the invoice…" forever,
  // retrying in a loop and never showing the error (staging, 2026-07-27).
  const fetchedForRef = useRef(null);
  useEffect(() => {
    if (!currentItem?.matter_id) return;
    if (payload) {
      fetchedForRef.current = currentItem.id;
      return;
    }
    if (fetchedForRef.current === currentItem.id) return;
    fetchedForRef.current = currentItem.id;
    dispatch(fetchFeeNoticeInvoice(currentItem.id));
  }, [dispatch, currentItem?.id, currentItem?.matter_id, payload]);

  useEffect(() => {
    setOverrideWarrant(false);
  }, [currentItem?.id]);

  // Seed the editable form each time a fresh payload lands (fetch or
  // save response). Keyed on the payload object identity so reviewer
  // keystrokes are never clobbered by a re-render.
  useEffect(() => {
    if (!invoice) return;
    setInvoiceNumber(invoice.invoice_number || "");
    setDecisionSentence(invoice.decision_sentence || "");
    setAward(invoice.retroactive_award || "");
    setFeePercent(invoice.fee_percent || "");
    setAttorneyFee(invoice.attorney_fee || "");
    setCostLines((invoice.cost_lines || []).map((line) => ({ ...line })));
    setRecipient((prev) => prev || invoice.client?.email || "");
    setDirty(false);
  }, [invoice]);

  const total = useMemo(() => lineTotal(costLines), [costLines]);

  const touch = () => setDirty(true);

  // Award x fee %% is the fee VA withholds; recompute the convenience
  // prefill on either input, but only when this letter has a fee at
  // all (no-fee letters keep it absent).
  const recomputeFee = (nextAward, nextPercent) => {
    if (invoice?.attorney_fee == null) return;
    const awardNum = Number.parseFloat(
      String(nextAward ?? "").replace(/[$,]/g, "")
    );
    const pctNum = Number.parseFloat(String(nextPercent ?? ""));
    if (Number.isFinite(awardNum) && Number.isFinite(pctNum)) {
      setAttorneyFee(((awardNum * pctNum) / 100).toFixed(2));
    }
  };

  const buildEdited = useCallback(
    () => ({
      ...invoice,
      invoice_number: invoiceNumber.trim(),
      decision_sentence: decisionSentence.trim(),
      retroactive_award: award === "" ? null : String(award).replace(/[$,]/g, ""),
      fee_percent: feePercent,
      attorney_fee:
        attorneyFee === "" ? null : String(attorneyFee).replace(/[$,]/g, ""),
      cost_lines: costLines
        .filter((line) => (line.label || "").trim() || line.amount)
        .map((line) => ({
          ...line,
          label: (line.label || "").trim(),
          amount: String(line.amount ?? "").replace(/[$,]/g, ""),
        })),
      client: { ...(invoice?.client || {}), email: recipient.trim() },
    }),
    [invoice, invoiceNumber, decisionSentence, award, feePercent,
     attorneyFee, costLines, recipient]
  );

  const handleSave = async () => {
    const result = await dispatch(
      saveFeeNoticeInvoice({ id: currentItem.id, invoice: buildEdited() })
    );
    if (saveFeeNoticeInvoice.fulfilled.match(result)) {
      message.success("Invoice saved — email preview updated");
    } else {
      message.error(result.payload || "Failed to save the invoice");
    }
  };

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      // Unsaved edits render as-shown; otherwise the backend uses the
      // saved (or freshly assembled) payload.
      const response = await downloadFeeNoticeInvoicePdfAPI(
        currentItem.id,
        dirty ? buildEdited() : null
      );
      const url = URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `Cost Invoice ${invoiceNumber || currentItem.id}.pdf`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      message.error(error.message || "Failed to generate the invoice PDF");
    } finally {
      setDownloading(false);
    }
  };

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(payload?.email_body || "");
      message.success("Email text copied");
    } catch {
      message.error("Couldn't copy — select the preview text instead");
    }
  };

  const handleCopyPaymentLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      message.success("Payment link copied");
    } catch {
      message.error("Couldn't copy — select the link text instead");
    }
  };

  const handleSend = () => {
    Modal.confirm({
      title: "Send this invoice to the client?",
      content: `The email and PDF statement will be sent to ${recipient}.`,
      okText: "Send",
      onOk: async () => {
        const result = await dispatch(
          sendFeeNoticeInvoice({ id: currentItem.id, to: recipient.trim() })
        );
        if (sendFeeNoticeInvoice.fulfilled.match(result)) {
          message.success(`Invoice sent to ${recipient}`);
        } else {
          message.error(result.payload || "Failed to send the invoice");
        }
      },
    });
  };

  if (!currentItem?.matter_id) {
    return (
      <FieldGroup>
        <SectionHeading>Cost Invoice</SectionHeading>
        <MutedText>
          Confirm the matter match on the Review tab to unlock the invoice.
        </MutedText>
      </FieldGroup>
    );
  }

  if (invoiceLoading || (!payload && !invoiceError)) {
    return <LoadingState>Assembling the invoice…</LoadingState>;
  }

  if (invoiceError && !payload) {
    return (
      <FieldGroup>
        <DegradedBanner>{invoiceError}</DegradedBanner>
        <BtnRow>
          <SecondaryBtn
            onClick={() => dispatch(fetchFeeNoticeInvoice(currentItem.id))}
          >
            Retry
          </SecondaryBtn>
        </BtnRow>
      </FieldGroup>
    );
  }

  // Plenty of SOC/FAN letters warrant no invoice at all — say so
  // outright rather than presenting a form over a $0 award (Matthew,
  // staging item 67: benefits granted with no change to the combined
  // evaluation, so no past-due benefits to reimburse costs from). The
  // backend verdict is advisory: extraction does miss awards, so the
  // reviewer can override. A letter already saved or sent skips the
  // gate — that decision has been made.
  const warrant = payload?.warrant;
  const notWarranted =
    warrant?.warranted === false &&
    !payload?.saved &&
    !(payload?.sends?.length > 0);

  if (notWarranted && !overrideWarrant) {
    return (
      <FieldGroup>
        <WarningBanner>
          <WarningHeader>
            <WarningLabel>Cost invoice not warranted</WarningLabel>
          </WarningHeader>
          <WarningBody>
            This letter warrants no cost invoice because {warrant.reason}
          </WarningBody>
        </WarningBanner>
        <MutedText>
          There is nothing to send — finish the letter on the Review tab.
        </MutedText>
        <BtnRow>
          <SecondaryBtn
            type="button"
            onClick={() => setOverrideWarrant(true)}
          >
            Prepare one anyway
          </SecondaryBtn>
        </BtnRow>
      </FieldGroup>
    );
  }

  const busy = disabled || invoiceSaving || invoiceSending;
  const lastSend = payload?.last_send;
  // Payload paid state (webhook-maintained) + the live payment link
  // minted on send. Either alone warrants the Payment block: a link
  // exists as soon as the invoice is sent, and a paid state can exist
  // on old links whose letter was since edited.
  const paymentView = describePaymentStatus(payload?.payment_status);
  const paymentLink = invoice?.payment_link;
  const paymentEvents = payload?.payments?.length || 0;
  // Sending requires a STORED invoice, so an assembled-but-never-saved
  // one needs saving even with nothing edited. Gating Save on `dirty`
  // alone deadlocked exactly the common case: a reviewer who agrees with
  // every assembled number could not save, so could never send.
  const saveNeeded = dirty || !payload?.saved;
  // The first press is not a save, it CREATES the invoice: it assigns the
  // number and mints the client's payment link, and until it happens no
  // invoice exists to send. Saying "Save" made reviewers look for the
  // thing that creates one (Matthew, 2026-08-27: "I think I'd prefer a
  // 'Create Invoice' button rather than 'Save'. What's it saving?").
  const invoiceExists = Boolean(payload?.saved);
  // Named rather than "the client": a warning that says who has been left
  // waiting is one a reviewer acts on.
  const client = invoice?.client || {};
  const clientLabel = (
    client.name
    || `${client.first_name || ""} ${client.last_name || ""}`.trim()
    || recipient.trim()
  );
  const saveLabel = invoiceSaving
    ? invoiceExists
      ? "Saving…"
      : "Creating…"
    : !invoiceExists
      ? "Create invoice"
      : saveNeeded
        ? "Save changes"
        : "Saved";
  const sendBlockedReason = !payload?.send_available
    ? "Sending is not enabled yet — download the PDF or copy the email instead."
    : !invoiceExists
      ? "Create the invoice first — that assigns its number and payment link."
      : saveNeeded
        ? "Save your changes first so what you send is what's stored."
        : !recipient.trim()
          ? "Enter the client's email address."
          : "";

  return (
    <>
      {notWarranted && overrideWarrant && (
        <DegradedBanner>
          Preparing this against the letter&rsquo;s own numbers —{" "}
          {warrant.reason} Check the award and cost lines before sending.
        </DegradedBanner>
      )}

      {payload?.salesforce_error && (
        <DegradedBanner>
          Couldn&rsquo;t pull costs from Salesforce
          ({payload.salesforce_error}). Add the cost lines by hand below.
        </DegradedBanner>
      )}

      {lastSend && (
        <SentBanner>
          Sent to {lastSend.to}
          {lastSend.by_email ? ` by ${lastSend.by_email}` : ""}
          {lastSend.at
            ? ` on ${new Date(lastSend.at).toLocaleString()}`
            : ""}
          {(payload?.sends?.length || 0) > 1
            ? ` (${payload.sends.length} sends total)`
            : ""}
        </SentBanner>
      )}

      {/* The state the panel used to say nothing about. A created invoice
          has a live payment link and an invoice number, so everything on
          screen looks finished — and on 2026-08-28 four of them sat
          unsent because nothing contradicted that. */}
      {invoiceExists && !lastSend && (
        <UnsentBanner>
          NOT SENT{clientLabel ? ` — ${clientLabel} has` : " — the client has"}{" "}
          not received this invoice. Nothing has left the firm until you
          press <em>Send to client</em> below.
        </UnsentBanner>
      )}

      {!payload?.saved && !dirty && (
        <MutedText>
          Assembled fresh from the letter and Salesforce — review the
          numbers, reword the cost labels for the client, then press
          Create invoice.
        </MutedText>
      )}

      {(paymentView || paymentLink?.url) && (
        <FieldGroup>
          <SectionHeading>Payment</SectionHeading>
          {paymentView ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <PaymentPill tone={paymentView.tone}>
                {paymentView.label}
              </PaymentPill>
              {paymentView.detail && (
                <MetaText>{paymentView.detail}</MetaText>
              )}
            </div>
          ) : (
            <MutedText>
              No payments yet — the paid state updates automatically when
              the client pays through the link.
            </MutedText>
          )}
          {paymentLink?.url && (
            <>
              <FieldLabel>
                Payment link in the sent email
                {money(paymentLink.amount)
                  ? ` (for ${money(paymentLink.amount)})`
                  : ""}
              </FieldLabel>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 8,
                  minWidth: 0,
                }}
              >
                <MetaText
                  style={{ wordBreak: "break-all", minWidth: 0 }}
                >
                  {paymentLink.url}
                </MetaText>
                <LinkBtn
                  type="button"
                  onClick={() => handleCopyPaymentLink(paymentLink.url)}
                >
                  Copy
                </LinkBtn>
              </div>
            </>
          )}
          {paymentEvents > 0 && (
            <MutedText>
              {paymentEvents} payment event
              {paymentEvents === 1 ? "" : "s"} recorded.
            </MutedText>
          )}
        </FieldGroup>
      )}

      <FieldGroup>
        <SectionHeading>Decision Summary</SectionHeading>
        <TextInput
          as="textarea"
          rows={3}
          style={{ resize: "vertical", minHeight: 64 }}
          value={decisionSentence}
          placeholder="In its …, decision, VA awarded benefits for …"
          onChange={(e) => {
            setDecisionSentence(e.target.value);
            touch();
          }}
          disabled={busy}
        />
      </FieldGroup>

      <FieldGroup>
        <SectionHeading>Award &amp; Fee</SectionHeading>
        <FieldLabel>Retroactive award ($)</FieldLabel>
        <TextInput
          value={award}
          onChange={(e) => {
            setAward(e.target.value);
            recomputeFee(e.target.value, feePercent);
            touch();
          }}
          disabled={busy}
        />
        <FieldLabel>Fee %</FieldLabel>
        <TextInput
          value={feePercent}
          onChange={(e) => {
            setFeePercent(e.target.value);
            recomputeFee(award, e.target.value);
            touch();
          }}
          disabled={busy}
        />
        <FieldLabel>Attorney&rsquo;s fee paid by VA ($)</FieldLabel>
        <TextInput
          value={attorneyFee}
          placeholder="Blank when no fee was awarded"
          onChange={(e) => {
            setAttorneyFee(e.target.value);
            touch();
          }}
          disabled={busy}
        />
      </FieldGroup>

      <FieldGroup>
        <SectionHeading>Case Costs Advanced</SectionHeading>
        <CostLinesTable>
          {costLines.length === 0 && (
            <CostLineRow>
              <MutedText>No cost lines — add one below.</MutedText>
            </CostLineRow>
          )}
          {costLines.map((line, index) => (
            <CostLineRow key={line.sf_id || `new-${index}`}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <TextInput
                  value={line.label || ""}
                  placeholder="Client-facing description"
                  onChange={(e) => {
                    setCostLines((lines) =>
                      lines.map((l, i) =>
                        i === index ? { ...l, label: e.target.value } : l
                      )
                    );
                    touch();
                  }}
                  disabled={busy}
                />
                {(line.raw_name || line.date || line.memo) && (
                  <CostLineMeta>
                    {[line.raw_name, line.date, line.memo]
                      .filter(Boolean)
                      .join(" · ")}{" "}
                    (internal — not shown to the client)
                  </CostLineMeta>
                )}
              </div>
              <TextInput
                style={{ width: 90, textAlign: "right", flexShrink: 0 }}
                value={line.amount ?? ""}
                placeholder="0.00"
                onChange={(e) => {
                  setCostLines((lines) =>
                    lines.map((l, i) =>
                      i === index ? { ...l, amount: e.target.value } : l
                    )
                  );
                  touch();
                }}
                disabled={busy}
              />
              <LinkBtn
                type="button"
                title="Remove this line"
                onClick={() => {
                  setCostLines((lines) =>
                    lines.filter((_, i) => i !== index)
                  );
                  touch();
                }}
                disabled={busy}
              >
                <Trash size={15} />
              </LinkBtn>
            </CostLineRow>
          ))}
          <CostTotalRow>
            <span>Total Costs Due</span>
            <span>{money(total) || "$0.00"}</span>
          </CostTotalRow>
        </CostLinesTable>
        <BtnRow>
          <SecondaryBtn
            type="button"
            onClick={() => {
              setCostLines((lines) => [
                ...lines,
                { label: "", amount: "", sf_id: null },
              ]);
              touch();
            }}
            disabled={busy}
          >
            <Plus size={14} /> Add cost line
          </SecondaryBtn>
        </BtnRow>
      </FieldGroup>

      <FieldGroup>
        <SectionHeading>Invoice &amp; Recipient</SectionHeading>
        <FieldLabel>Invoice number</FieldLabel>
        <TextInput
          value={invoiceNumber}
          onChange={(e) => {
            setInvoiceNumber(e.target.value);
            touch();
          }}
          disabled={busy}
        />
        <FieldLabel>Client email</FieldLabel>
        <TextInput
          value={recipient}
          placeholder="client@example.com"
          onChange={(e) => {
            setRecipient(e.target.value);
            touch();
          }}
          disabled={busy}
        />
      </FieldGroup>

      <FieldGroup>
        <SectionHeading>
          Email Preview{invoiceExists && dirty ? " (updates after Save)" : ""}
        </SectionHeading>
        {/* Nothing renders before the invoice exists. The draft used to
            show here in full — greeting, itemized costs, total, and a
            real working payment URL (the firm's generic LawPay page,
            since this client's link is not minted yet) — and reviewers
            read all that finished-looking text as an invoice that had
            already been created and just needed sending. Matthew did
            exactly that on Hornbuckle (2026-08-28) and came away
            believing a Payload link already existed. His call on the
            fix: "We can't blank less - people won't read it, they see
            all that text and just assume it's done." */}
        {invoiceExists ? (
          <>
            {payload?.email_subject && (
              <MutedText style={{ fontWeight: 600 }}>
                Subject: {payload.email_subject}
              </MutedText>
            )}
            <EmailPreview>{payload?.email_body || ""}</EmailPreview>
            <BtnRow>
              <SecondaryBtn type="button" onClick={handleCopyEmail}>
                Copy email text
              </SecondaryBtn>
            </BtnRow>
          </>
        ) : (
          <MutedText>
            No invoice exists for this letter yet. Check the costs above,
            then press <strong>Create invoice</strong> — that assigns the
            invoice number, creates the client&rsquo;s payment link, and
            shows you the exact email to proofread here. It does not send
            anything.
          </MutedText>
        )}
      </FieldGroup>

      {/* Send comes FIRST and stands alone. Creating an invoice mints a
          real Payload link, so a screen that puts Create and Download
          above Send reads as though the work finishes there: on
          2026-08-28 four invoices were created by three people and none
          were sent, and no client was ever told. Matthew's call. */}
      <FieldGroup>
        <SendBtn
          type="button"
          onClick={handleSend}
          disabled={busy || Boolean(sendBlockedReason)}
          title={sendBlockedReason || undefined}
        >
          {invoiceSending
            ? "Sending…"
            : lastSend
              ? "Send again"
              : "Send to client"}
        </SendBtn>
        {sendBlockedReason && <MutedText>{sendBlockedReason}</MutedText>}
        <BtnRow>
          <SecondaryBtn
            type="button"
            onClick={handleSave}
            disabled={busy || !saveNeeded}
            title={
              invoiceExists
                ? undefined
                : "Assigns the invoice number and creates the client's payment link."
            }
          >
            {saveLabel}
          </SecondaryBtn>
          {/* Also gated on the invoice existing: the pre-creation PDF
              carries the same generic LawPay URL as the old preview did,
              and a downloaded PDF can be emailed by hand, so leaving it
              available would keep the exact trap open in a form that
              outlives the screen. */}
          <SecondaryBtn
            type="button"
            onClick={handleDownloadPdf}
            disabled={busy || downloading || !invoiceExists}
            title={
              invoiceExists
                ? undefined
                : "Create the invoice first — until then the PDF has no invoice number or payment link."
            }
          >
            {downloading ? "Rendering…" : "Download PDF"}
          </SecondaryBtn>
        </BtnRow>
      </FieldGroup>
    </>
  );
};

export default InvoiceSection;
